import React, { useMemo, useState, useEffect, useContext, useRef } from 'react';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import { T } from 'react-translator-component';
import useBookable from '../../hooks/useBookable';
import { format, parseISO } from 'date-fns';
import { getLocale } from '../../functions';
import { UserContext } from '../../context/user';

// Funciones UTC para evitar problemas con zona horaria de Argentina (-3)
const getUTCToday = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

const parseUTCDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    return new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
};

const toISOString = (date) => {
    if (!date) return null;
    return date.toISOString().split('T')[0]; // YYYY-MM-DD en UTC
};

const formatUTCDate = (date, language = 'es-ES') => {
    if (!date) return null;
    const day = date.getUTCDate();
    let locale = language;
    if (language === 'en') {
        locale = 'en-US';
    }
    if (language === 'es') {
        locale = 'es-ES';
    }
    if (language === 'fr') {
        locale = 'fr-FR';
    }
    if (language === 'it') {
        locale = 'it-IT';
    }
    if (language === 'de') {
        locale = 'de-DE';
    }
    //obtener el mes en el idioma del usuario pero con el utc time
    const month = date.toLocaleString(locale, { month: 'short', timeZone: 'UTC' });
    return { dayOfMonth: day, monthShort: month };
};

const addDaysUTC = (date, days) => {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
};

const QuestionSelectDay = ({
    hideCheckoutDate = false,
    startDate,
    endDate,
    multiple = false,
    value = null,
    onChange,
    bookableItemId = null,
    initialSelectedAll = false,
    useCheckinDate = false,
    useCheckoutDate = false,
    hideToday = false
}) => {
    const [selectedDays, setSelectedDays] = useState([]);
    const { getScheduleCustom } = useBookable();
    const [schedule, setSchedule] = useState([]);
    const [isLoading, setIsLoading] = useState(!!bookableItemId);
    const { language } = useContext(UserContext)
    // Inicializar con el valor actual si existe
    useEffect(() => {
        if (!bookableItemId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        let start = parseUTCDate(startDate);
        let end = parseUTCDate(endDate);
        if (useCheckinDate) {            //si useCheckinDate es true, el endDate es el mismo que el startDate
            end = start
        }
        if (useCheckoutDate) {
            //si useCheckoutDate es true, el startDate es el mismo que el endDate
            start = end;
        }
        getScheduleCustom(bookableItemId, start.toISOString().split('T')[0], end.toISOString().split('T')[0])
            .then(res => {
                console.log('19dic schedule', res);
                setSchedule(res);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error loading schedule:', error);
                setIsLoading(false);
            })
    }, [bookableItemId]);

    useEffect(() => {
        if (value !== undefined && value !== null) {
            if (multiple && Array.isArray(value)) {
                setSelectedDays(value);
            } else if (!multiple && value) {
                setSelectedDays([value]);
            } else if (Array.isArray(value) && value.length === 0) {
                // Si value es un array vacío, limpiar selectedDays
                setSelectedDays([]);
            }
        }
    }, [value, multiple]);


    // Generar array de fechas entre startDate y endDate (UTC+0)
    const daysArray = useMemo(() => {
        const days = [];
        if (!startDate || !endDate) {
            console.log('QuestionSelectDay: startDate o endDate no definidos', { startDate, endDate });
            return days;
        }

        try {
            const today = getUTCToday(); // Usar UTC para hoy

            const start = parseUTCDate(startDate);
            const end = parseUTCDate(endDate);

            console.log('QuestionSelectDay: Generando días entre', { startDate, endDate, start, end, today });

            // Asegurarse de que las fechas sean válidas
            if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
                console.error('QuestionSelectDay: Fechas inválidas', { start, end });
                return days;
            }

            // Si la fecha de inicio es anterior a hoy, comenzar desde hoy
            const effectiveStart = start < today ? new Date(today) : new Date(start);

            // Crear un mapa de fechas del schedule para acceso rápido
            const scheduleMap = new Map(schedule.map(s => [s.date, s.hours]));

            // Iterar desde start hasta end
            for (let d = new Date(effectiveStart); d <= end; d = addDaysUTC(d, 1)) {
                if (hideToday && d.toISOString() === today.toISOString()) {
                    continue;
                }
                const dateFormatted = formatUTCDate(d, language);
                const dateString = toISOString(d);

                let isAvailable = false;
                if (schedule.length === 0 && bookableItemId != null) {
                    // Si viene el schedule vacio, no hay stock disponible
                    isAvailable = false;
                } else if (scheduleMap.has(dateString) && bookableItemId != null) {
                    // Verifica si hay stock disponible
                    const isReservationAvailableSlot = scheduleMap.get(dateString);
                    isAvailable = isReservationAvailableSlot.some(slot => slot.reservationsCount < slot.maxReservations && slot.slotsAvailable > 0);
                }

                // Si no viene el bookableItemId, es un upselling, hay stock disponible
                if (bookableItemId == null) {
                    isAvailable = true;
                }

                days.push({
                    date: new Date(d),
                    dateString: dateString, // YYYY-MM-DD en UTC
                    dayOfMonth: dateFormatted.dayOfMonth,
                    monthShort: dateFormatted.monthShort,
                    available: isAvailable
                });
            }

        } catch (error) {
            console.error('QuestionSelectDay: Error al generar días', error);
        }

        return days;
    }, [startDate, endDate, schedule]);

    // Días que se muestran como botones (ocultar checkout si hideCheckoutDate). Siempre definido para cumplir rules-of-hooks.
    const visibleDays = useMemo(
        () => (hideCheckoutDate && endDate
            ? daysArray.filter(day => day.dateString !== endDate)
            : daysArray),
        [daysArray, hideCheckoutDate, endDate]
    );
    const visibleSelectedCount = visibleDays.filter(day => selectedDays.includes(day.dateString)).length;

    const handleDayClick = (dateString, available = true) => {
        // No permitir seleccionar días no disponibles
        if (!available) {
            return;
        }

        let newSelectedDays = [...selectedDays];

        if (multiple) {
            // Modo múltiple: agregar o remover
            if (newSelectedDays.includes(dateString)) {
                newSelectedDays = newSelectedDays.filter(d => d !== dateString);
            } else {
                newSelectedDays.push(dateString);
            }
        } else {
            // Modo único: reemplazar
            if (newSelectedDays.includes(dateString)) {
                newSelectedDays = [];
            } else {
                newSelectedDays = [dateString];
            }
        }

        setSelectedDays(newSelectedDays);

        // Notificar al padre con el valor correcto
        if (onChange) {
            //obtener un array de meses unicos con la cantidad de dias seleccionados cada mes que se repitan se suman
            console.log('26dic newSelectedDays', newSelectedDays.map(day => day));
            
            let selectedMonths = {};
            if (newSelectedDays && newSelectedDays.length > 0) {
                selectedMonths = newSelectedDays
                    .map(day => day.split('-')[1])
                    .filter(month => month !== undefined)
                    .reduce((acc, month) => {
                        acc[month] = (acc[month] || 0) + 1;
                        return acc;
                    }, {});
            }
            console.log('26dic selectedMonths', selectedMonths);

            if (multiple) {
                onChange(newSelectedDays, selectedMonths);
            } else {
                onChange(newSelectedDays.length > 0 ? newSelectedDays[0] : '', selectedMonths);
            }
        }
    };

    // Pre-seleccionar todos los días disponibles si initialSelectedAll es true (solo una vez al montar cuando hay días)
    const hasRunInitialSelectedAll = useRef(false);
    useEffect(() => {
        if (initialSelectedAll && !hasRunInitialSelectedAll.current && selectedDays.length === 0 && daysArray.length > 0) {
            hasRunInitialSelectedAll.current = true;
            const availableDays = daysArray.filter(day => day.available).map(day => day.dateString);
            setSelectedDays(availableDays);
            if (onChange) {
                onChange(multiple ? availableDays : (availableDays.length > 0 ? availableDays[0] : ''));
            }
        }
    }, [daysArray, initialSelectedAll, selectedDays.length]);

    // Cuando useCheckinDate es true, auto-seleccionar la fecha de checkinStart si está disponible
    useEffect(() => {
        if ((useCheckinDate || useCheckoutDate) && daysArray.length > 0) {
            const date = useCheckinDate ? startDate : endDate;
            const day = daysArray.find(day => day.dateString === date);

            if (day && day.available) {
                // La fecha está disponible, guardarla como seleccionada
                setSelectedDays([date]);
                if (onChange) {
                    // Pasar como array para mantener compatibilidad con Settings.js
                    onChange([date], {});
                }
            } else if (day && !day.available) {
                // La fecha existe pero no está disponible
                setSelectedDays([]);
            }
        }
    }, [useCheckinDate, useCheckoutDate, startDate, endDate, daysArray]);

    // Si useCheckinDate es true, mostrar vista simplificada
    if (useCheckinDate || useCheckoutDate) {
        const date = useCheckinDate ? startDate : endDate;
        const day = daysArray.find(day => day.dateString === date);

        return (
            <>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : day && day.available ? (
                    // Fecha disponible
                    <></>
                ) : (
                    // Fecha no disponible
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#fff2f0',
                        borderRadius: '4px',
                        border: '1px solid #ffccc7',
                        alignItems: 'center',
                        color: '#cf1322'
                    }}>
                        <span style={{
                            fontSize: '1.2em'
                        }}>
                            ⚠️
                        </span>
                        <span>
                            {T('upselling.no-availability')}
                        </span>
                    </div>
                )}
            </>
        );
    }

    return (
        <div className='upsellings-questions-item select-day-question' style={{ marginTop: '10px' }}>


            <Row className='align-items-center d-flex justify-content-between m-0 mb-3 row'>
                <div>
                    <span className='text-left'>
                        {multiple ? T('general.select-days') : T('general.select-day')}
                    </span>
                </div>
                {visibleSelectedCount > 0 && (
                    <div className='selected-day-question'>
                        <span className='text-left'>
                            {visibleSelectedCount === 1 ? `1 ${T('general.selected-day')}` : `${visibleSelectedCount} ${T('general.selected-days')}`}
                        </span>
                    </div>
                )}
            </Row>
            <Row>
                <Col xs={12}>
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Spinner animation="border" size="sm" />
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {visibleDays.map((day, idx) => (
                                <Button
                                    className='select-day-question-button'
                                    key={`day-${idx}-${day.dateString}`}
                                    variant={day.available && selectedDays.includes(day.dateString) ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => handleDayClick(day.dateString, day.available)}
                                    disabled={!day.available}
                                    style={{
                                        padding: '8px 12px',
                                        minWidth: '70px',
                                        fontSize: '0.9em',
                                        opacity: day.available ? 1 : 0.5,
                                        cursor: day.available ? 'pointer' : 'not-allowed',
                                        backgroundColor: !day.available ? '#e9ecef' : undefined,
                                        borderColor: !day.available ? '#dee2e6' : undefined,
                                        color: !day.available ? '#6c757d' : undefined
                                    }}
                                >
                                    <div>{day.dayOfMonth} {day.monthShort}</div>
                                </Button>
                            ))}
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default QuestionSelectDay;

